import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { personalDataApplicationsState } from './initialState';
import {
  createApplicationPersonalDataClient,
  createApplicationPersonalDataClientRevoke,
  createApplicationPersonalDataManager,
} from './thunks/personalDataApplicationsThunks.ts';
import {
  personalDataApplicationsSubmitFulfilled,
  personalDataApplicationsSubmitPending,
} from './actions/personalDataApplicationsActions.ts';
import { personalDataApplicationsError } from '~/store/processing-personal/actions/actions.ts';
import {
  postApplicationForm,
  revokePostApplicationForm,
} from '~/store/processing-personal/thunks/applicationFormThunks.ts';
import {
  postApplicationFormFulfilled,
  postApplicationFormPending,
} from '~/store/processing-personal/actions/applicationFormActions.ts';
import { whyRevokeApplicationForm } from '~/store/processing-personal/thunks/whyRevokeApplicationFormThunks.ts';
import {
  whyApplicationFormRevokeFulfilled,
  whyApplicationFormRevokePending,
} from '~/store/processing-personal/actions/whyRevokeApplicationFormActions.ts';
import {
  getApplicationForm,
  revokeGetApplicationForm,
} from '~/store/processing-personal/thunks/getApplicationFormThunks.ts';
import {
  getApplicationFormFulfilled,
  getApplicationFormPending,
} from '~/store/processing-personal/actions/getApplicationFormActions.ts';
import {
  documentPreviewFulfilled,
  documentPreviewPending,
} from '~/store/processing-personal/actions/documentPreviewPersonalActions.ts';
import {
  documentPreviewPersonalData,
  documentPreviewPersonalDataTable,
  revokeDocumentPreviewPersonalData,
  revokeDocumentPreviewPersonalDataTable,
} from '~/store/processing-personal/thunks/documentPreviewPersonalThunks.ts';
import { validateDocumentProcessingPersonal } from '~/store/processing-personal/thunks/validateDocumentProcessingPersonalThunks.ts';
import {
  validateDocumentFulfilled,
  validateDocumentPending,
} from '~/store/processing-personal/actions/validateDocumentProcessingPersonalActions.ts';
import { revokeValidateDocumentProcessingPersonal } from '~/store/processing-personal/thunks/validateDocumentProcessingPersonalThunks.ts';
import {
  getPersonalDataTableRevoke,
  getPersonalDataTableSubmit,
} from '~/store/processing-personal/thunks/personalDataTableThunks.ts';
import {
  personalDataTableRevokeFulfilled,
  personalDataTableRevokePending,
  personalDataTableRevokeRejected,
  personalDataTableSubmitFulfilled,
  personalDataTableSubmitPending,
  personalDataTableSubmitRejected,
} from '~/store/processing-personal/actions/personalDataTableActions.ts';

const personalDataApplicationsSlice = createSlice({
  name: 'processingPersonalDataApplications',
  initialState: personalDataApplicationsState,
  reducers: {
    clearPersonalDataApplicationsState() {
      return personalDataApplicationsState;
    },
    setPersonalDataApplicationsError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        createApplicationPersonalDataManager.fulfilled,
        personalDataApplicationsSubmitFulfilled
      )
      .addCase(createApplicationPersonalDataManager.pending, personalDataApplicationsSubmitPending)
      .addCase(createApplicationPersonalDataManager.rejected, personalDataApplicationsError)

      .addCase(
        createApplicationPersonalDataClient.fulfilled,
        personalDataApplicationsSubmitFulfilled
      )
      .addCase(createApplicationPersonalDataClient.pending, personalDataApplicationsSubmitPending)
      .addCase(createApplicationPersonalDataClient.rejected, personalDataApplicationsError)

      .addCase(
        createApplicationPersonalDataClientRevoke.fulfilled,
        personalDataApplicationsSubmitFulfilled
      )
      .addCase(
        createApplicationPersonalDataClientRevoke.pending,
        personalDataApplicationsSubmitPending
      )
      .addCase(createApplicationPersonalDataClientRevoke.rejected, personalDataApplicationsError)

      .addCase(postApplicationForm.fulfilled, postApplicationFormFulfilled)
      .addCase(postApplicationForm.pending, postApplicationFormPending)
      .addCase(postApplicationForm.rejected, personalDataApplicationsError)

      .addCase(revokePostApplicationForm.fulfilled, postApplicationFormFulfilled)
      .addCase(revokePostApplicationForm.pending, postApplicationFormPending)
      .addCase(revokePostApplicationForm.rejected, personalDataApplicationsError)

      .addCase(whyRevokeApplicationForm.fulfilled, whyApplicationFormRevokeFulfilled)
      .addCase(whyRevokeApplicationForm.pending, whyApplicationFormRevokePending)
      .addCase(whyRevokeApplicationForm.rejected, personalDataApplicationsError)

      .addCase(getApplicationForm.fulfilled, getApplicationFormFulfilled)
      .addCase(getApplicationForm.pending, getApplicationFormPending)
      .addCase(getApplicationForm.rejected, personalDataApplicationsError)

      .addCase(revokeGetApplicationForm.fulfilled, getApplicationFormFulfilled)
      .addCase(revokeGetApplicationForm.pending, getApplicationFormPending)
      .addCase(revokeGetApplicationForm.rejected, personalDataApplicationsError)

      .addCase(documentPreviewPersonalData.fulfilled, documentPreviewFulfilled)
      .addCase(documentPreviewPersonalData.pending, documentPreviewPending)
      .addCase(documentPreviewPersonalData.rejected, personalDataApplicationsError)

      .addCase(revokeDocumentPreviewPersonalData.fulfilled, documentPreviewFulfilled)
      .addCase(revokeDocumentPreviewPersonalData.pending, documentPreviewPending)
      .addCase(revokeDocumentPreviewPersonalData.rejected, personalDataApplicationsError)

      .addCase(documentPreviewPersonalDataTable.fulfilled, documentPreviewFulfilled)
      .addCase(documentPreviewPersonalDataTable.pending, documentPreviewPending)
      .addCase(documentPreviewPersonalDataTable.rejected, personalDataApplicationsError)

      .addCase(revokeDocumentPreviewPersonalDataTable.fulfilled, documentPreviewFulfilled)
      .addCase(revokeDocumentPreviewPersonalDataTable.pending, documentPreviewPending)
      .addCase(revokeDocumentPreviewPersonalDataTable.rejected, personalDataApplicationsError)

      .addCase(validateDocumentProcessingPersonal.fulfilled, validateDocumentFulfilled)
      .addCase(validateDocumentProcessingPersonal.pending, validateDocumentPending)
      .addCase(validateDocumentProcessingPersonal.rejected, personalDataApplicationsError)

      .addCase(revokeValidateDocumentProcessingPersonal.fulfilled, validateDocumentFulfilled)
      .addCase(revokeValidateDocumentProcessingPersonal.pending, validateDocumentPending)
      .addCase(revokeValidateDocumentProcessingPersonal.rejected, personalDataApplicationsError)

      .addCase(getPersonalDataTableSubmit.fulfilled, personalDataTableSubmitFulfilled)
      .addCase(getPersonalDataTableSubmit.pending, personalDataTableSubmitPending)
      .addCase(getPersonalDataTableSubmit.rejected, personalDataTableSubmitRejected)

      .addCase(getPersonalDataTableRevoke.fulfilled, personalDataTableRevokeFulfilled)
      .addCase(getPersonalDataTableRevoke.pending, personalDataTableRevokePending)
      .addCase(getPersonalDataTableRevoke.rejected, personalDataTableRevokeRejected);
  },
});

export const { clearPersonalDataApplicationsState, setPersonalDataApplicationsError } =
  personalDataApplicationsSlice.actions;

export default personalDataApplicationsSlice.reducer;
